import { XMarkIcon } from '@heroicons/react/24/solid';
import React, { FunctionComponent, KeyboardEvent, useState } from 'react';
import { classNames } from '../../utils/class-names';

export type TagInputAddMethod = 'default' | 'blur' | 'paste';

interface TagInputProps {
  values: string[];
  placeholder?: string;
  /**
   * Called before committing with the WOULD-BE full tag list (current +
   * new); return false to reject the addition. Deliberately different from
   * Blueprint's TagInput (which passed only the newly-added tags): seeing
   * the total lets callers enforce list-size limits, which the Blueprint
   * contract could not express.
   */
  onAdd?: (nextValues: string[], method: TagInputAddMethod) => boolean | void;
  onChange: (values: string[]) => void;
  /** Observe the uncommitted draft text (e.g. to preview exact-match input). */
  onDraftChange?: (draft: string) => void;
  addOnBlur?: boolean;
  className?: string;
  /** Extra classes for an individual chip (e.g. exact-match keyword styling). */
  getTagClassName?: (tag: string) => string;
  maxLength?: number;
}

/**
 * Comma/Enter separated chips input (replaces the Blueprint TagInput used
 * by the new-brief panel).
 */
// Case-insensitive tag equality ("AI" is the same tag as "ai"), matching the
// dedupe semantics the brief-keyword editors have always used. Accents stay
// significant ("café" and "cafe" are different keywords) — hence 'accent',
// not 'base', which would fold diacritics too.
export const sameTag = (a: string, b: string) =>
  a.localeCompare(b, undefined, { sensitivity: 'accent' }) === 0;

// Shared chip styling for exact-match brief keywords, so the exact-match
// look lives in one place across the keyword editors.
export const getExactMatchTagClassName = (
  keywords: Array<{ keyword?: string; exactMatch?: boolean }>,
  tag: string
) => ((keywords || []).find(x => x.keyword === tag)?.exactMatch ? 'underline font-bold' : '');

const TagInput: FunctionComponent<TagInputProps> = ({
  values,
  placeholder,
  onAdd,
  onChange,
  onDraftChange,
  addOnBlur,
  className,
  getTagClassName,
  maxLength,
}) => {
  const [draft, setDraft] = useState('');

  const updateDraft = (value: string) => {
    setDraft(value);
    onDraftChange?.(value);
  };

  /**
   * Returns false when `onAdd` rejected the addition; the draft is kept in
   * that case so the user's text is never silently lost.
   */
  const commitDraft = (method: TagInputAddMethod): boolean => {
    const newTags = draft
      .split(',')
      .map(x => x.trim())
      // Dedupe against existing values AND within the batch itself
      // ("a,a" must add one chip; chips are keyed by value).
      .filter(
        (x, i, batch) =>
          x.length > 0 &&
          !values.some(v => sameTag(v, x)) &&
          batch.findIndex(b => sameTag(b, x)) === i
      );

    if (newTags.length === 0) {
      updateDraft('');
      return true;
    }

    const next = [...values, ...newTags];
    if (onAdd && onAdd(next, method) === false) {
      return false;
    }

    onChange(next);
    updateDraft('');
    return true;
  };

  const removeTag = (tag: string) => {
    onChange(values.filter(x => x !== tag));
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' || event.key === ',') {
      const committed = commitDraft('default');
      // A rejected comma is left unconsumed so it types into the input: the
      // draft keeps accumulating ("beta, gamma") and commits as a batch once
      // an addition is accepted again (e.g. after an in-flight save lands).
      if (committed || event.key === 'Enter') {
        event.preventDefault();
      }
    } else if (event.key === 'Backspace' && draft.length === 0 && values.length > 0) {
      removeTag(values[values.length - 1]);
    }
  };

  return (
    <div
      className={classNames(
        'flex flex-wrap items-center gap-1.5 w-full rounded-md border border-gray-300 bg-white px-2 py-1.5 shadow-xs focus-within:border-secondary focus-within:ring-1 focus-within:ring-secondary',
        className || ''
      )}
    >
      {values.map(tag => (
        <span
          key={tag}
          className={classNames(
            'inline-flex items-center gap-1 rounded-sm bg-primary px-2 py-0.5 text-xs font-medium text-white',
            getTagClassName ? getTagClassName(tag) : ''
          )}
        >
          {tag}
          <button
            type="button"
            aria-label={`Remove ${tag}`}
            className="text-white hover:text-gray-200 focus:outline-hidden"
            onClick={() => removeTag(tag)}
          >
            <XMarkIcon className="h-3 w-3" aria-hidden="true" />
          </button>
        </span>
      ))}
      <input
        type="text"
        value={draft}
        maxLength={maxLength}
        placeholder={values.length === 0 ? placeholder : undefined}
        className="flex-1 min-w-[140px] border-0 p-0.5 text-sm focus:outline-hidden focus:ring-0"
        onChange={e => updateDraft(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={() => {
          if (addOnBlur && draft.trim().length > 0) {
            commitDraft('blur');
          }
        }}
      />
    </div>
  );
};

export default TagInput;
